import React, { FC, useState, useEffect } from 'react';
import { Box, Typography, IconButton, useMediaQuery, Tooltip } from '@mui/material';
import { Visibility, AttachFile, MarkUnreadChatAlt } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { useQuery, useMutation } from 'react-query';
import { formatShortFriendlyDateWithTime } from '../../../../helpers';
import { IBuildMessage, IResponse, MessageReadStatus } from '../../../../models';
import { Table, Pagination } from '../../../../components';
import {
  getBuildCommunicationMessages,
  markBuildCommunicationMessageAsRead
} from '../../../../fetch';

interface IMessageHistoryProps {
  buildId: number;
  onMessageSelect: (message: IBuildMessage) => void;
  selectedMessage?: IBuildMessage;
  onLoadingChange?: (isLoading: boolean) => void;
}

export const MessageHistory: FC<IMessageHistoryProps> = ({
  buildId,
  onMessageSelect,
  selectedMessage,
  onLoadingChange
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [locallyMarkedAsReadIds, setLocallyMarkedAsReadIds] = useState<Set<number>>(new Set());

  const {
    data: messagesResponse,
    isLoading,
    isFetching
  } = useQuery<IResponse<IBuildMessage[]>, Error>(
    ['getBuildCommunicationMessages', buildId, page, perPage],
    () =>
      getBuildCommunicationMessages(buildId, {
        sortBy: 'CreatedAt',
        sortDirection: 'Desc',
        page: page + 1,
        perPage: perPage
      }),
    {
      enabled: !!buildId,
      onSuccess: data => {
        setRecordCount(data.totalRecordCount || 0);
        // Select the latest message by default if no message is selected
        if (data.records && data.records.length > 0 && !selectedMessage) {
          onMessageSelect(data.records[0]);
        }
      }
    }
  );

  const { mutate: markAsRead } = useMutation({
    mutationFn: ({ messageId }: { messageId: number }) =>
      markBuildCommunicationMessageAsRead(buildId, messageId, MessageReadStatus.Read),
    onError: (error: any) => {
      enqueueSnackbar(error?.Detail ?? 'Failed to mark message as read, please try again.', {
        variant: 'error'
      });
    }
  });

  const messages = messagesResponse?.records || [];

  useEffect(() => {
    onLoadingChange?.(isLoading || isFetching);
  }, [isLoading, isFetching, onLoadingChange]);

  const handleViewMessage = (message: IBuildMessage) => {
    onMessageSelect(message);
  };

  const handleMarkAsRead = (messageId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    // Immediately hide the button locally
    setLocallyMarkedAsReadIds(prev => new Set(prev).add(messageId));
    markAsRead({ messageId });
  };

  const handleDownloadImage = (fileUrl: string, fileName?: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName || 'message-attachment';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      Header: '',
      accessor: 'unreadStatus',
      overrideWidth: 40,
      Cell: ({ cell: { row } }: { cell: { row: { original: IBuildMessage } } }) => {
        const message = row.original;
        // Show button only if message is unread AND not locally marked as read
        const isUnread =
          message.buildTeamMessageReadStatus === MessageReadStatus.Unread &&
          !locallyMarkedAsReadIds.has(message.buildCommunicationMessagesId);
        if (isUnread) {
          return (
            <Tooltip title="Mark as read">
              <IconButton
                size="small"
                onClick={e => handleMarkAsRead(message.buildCommunicationMessagesId, e)}
                className={classes.unreadIcon}
              >
                <MarkUnreadChatAlt fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        }
        return null;
      }
    },
    {
      Header: '',
      accessor: 'createdBy',
      Cell: ({
        cell: { value, row }
      }: {
        cell: { value: string; row: { original: IBuildMessage } };
      }) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {value || 'Unknown'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {formatShortFriendlyDateWithTime(row.original.createdAt)}
          </Typography>
        </Box>
      )
    },
    {
      Header: '',
      accessor: 'message',
      Cell: ({ cell: { value } }: { cell: { value: string } }) => {
        const maxLength = isMobile ? 10 : 50;
        const displayText =
          value && value.length > maxLength ? `${value.substring(0, maxLength)}...` : value;

        return (
          <Typography variant="body2" className={classes.messagePreview} title={value}>
            {displayText}
          </Typography>
        );
      }
    },
    {
      Header: '',
      accessor: 'actions',
      Cell: ({ cell: { row } }: { cell: { row: { original: IBuildMessage } } }) => (
        <Box display="flex" gap={0.5} className={classes.actionsContainer}>
          {row.original.fileUrl && (
            <IconButton
              size="small"
              className={classes.actionIcon}
              onClick={() => handleDownloadImage(row.original.fileUrl)}
              title="Download attachment"
            >
              <AttachFile fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => handleViewMessage(row.original)}
            className={classes.viewIcon}
            title="View message"
          >
            <Visibility fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box className={classes.historyContainer}>
      <Typography variant="h6" style={{ marginBottom: messages.length > 0 ? -12 : 0 }}>
        History
      </Typography>

      {messages.length === 0 && !isLoading && !isFetching ? (
        <Typography variant="body2" color="textSecondary">
          No messages yet.
        </Typography>
      ) : (
        <>
          <Table
            columns={columns}
            data={messages}
            isLoading={isLoading || isFetching}
            hidePagination
            tableSize="small"
            rowClasses={classes.compactRow}
          />
          {!isLoading && !isFetching && (
            <Pagination
              page={page}
              count={recordCount}
              rowsPerPage={perPage}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
            />
          )}
        </>
      )}
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  historyContainer: {
    padding: theme.spacing(0.25),
    height: 'fit-content',
    maxHeight: '600px',
    overflowY: 'auto'
  },
  messagePreview: {
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  viewIcon: {
    color: theme.palette.primary.main
  },
  actionIcon: {
    color: theme.palette.grey[600]
  },
  compactRow: {
    '& .MuiTableCell-root': {
      padding: theme.spacing(0.5, 1)
    }
  },
  actionsContainer: {
    width: '80px',
    justifyContent: 'flex-end'
  },
  unreadIcon: {
    color: theme.palette.secondary.main
  }
}));
